import React, { useState, useEffect } from "react";
import axiosInstance from "../utlis/axiosInstance";
import styles from "./RoleManagement.module.css";
import { useUser } from "../provider/UserProvider";
import {
  Business as BusinessIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { Role } from "../data/roles";

// API endpoint
const ROLE_API = "/gpstracker/rolehandler/";
const PERMISSION_API = "/gpstracker/permissionhandler/";

// Interface for API response
interface ApiResponse {
  responseData: any;
  status: boolean;
  message: string;
}

// Interface for API role
interface ApiRole {
  id: number;
  name: string;
}

// Interface for API permission
interface ApiPermission {
  id: number;
  name: string;
}

// New role form data interface
interface RoleFormData {
  name: string;
}

const RoleManagement: React.FC = () => {
  const { permissions: userPermissions, user } = useUser();
  
  // Check if user is admin
  const isAdmin = user?.user?.administrator;
  
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<ApiPermission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New form data state
  const [formData, setFormData] = useState<RoleFormData>({
    name: "",
  });

  // Fetch roles from API - only for admin users
  useEffect(() => {
    if (isAdmin) {
      fetchRoles();
      fetchPermissions();
    }
  }, [isAdmin]);

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<ApiResponse>(ROLE_API, {
        params: {
          offset: 0,
          limit: 5000,
        },
      });

      if (response.data.status && response.data.responseData) {
        // Transform API roles to match our application's Role interface
        const apiRoles = response.data.responseData as ApiRole[];
        const transformedRoles: Role[] = apiRoles.map((apiRole) => ({
          id: apiRole.id,
          name: apiRole.name,
          type: "custom",
          permissions: [], // Will be populated when user clicks Link
          users: 0, // API doesn't provide this yet
          lastModified: new Date().toISOString().split("T")[0],
        }));

        setRoles(transformedRoles);
      } else {
        setError("Failed to fetch roles: " + response.data.message);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError("Failed to fetch roles. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions from API
  const fetchPermissions = async () => {
    setPermissionLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<ApiResponse>(PERMISSION_API, {
        params: {
          offset: 0,
          limit: 10000,
        },
      });

      if (response.data.status && response.data.responseData) {
        const apiPermissions = response.data.responseData as ApiPermission[];
        setPermissions(apiPermissions);
      } else {
        setError("Failed to fetch permissions: " + response.data.message);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setError("Failed to fetch permissions. Please try again later.");
    } finally {
      setPermissionLoading(false);
    }
  };

  // Fetch role permissions from API
  const fetchRolePermissions = async (roleId: number) => {
    setPermissionLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<ApiResponse>(
        `/gpstracker/rolepermissionshandler/${roleId}`
      );

      if (response.data.status && response.data.responseData) {
        // Extract permissions from the response structure
        const { permissions } = response.data.responseData;
        if (permissions && Array.isArray(permissions)) {
          // Extract permission IDs from the permissions array
          const permissionIds = permissions.map((permission: any) => permission.id);
          setSelectedPermissions(permissionIds);

          // Update the role's permissions in local state
          setRoles(prevRoles =>
            prevRoles.map(role =>
              role.id === roleId
                ? { ...role, permissions: permissionIds.map(id => id.toString()) }
                : role
            )
          );
        } else {
          setSelectedPermissions([]);
          // Update the role's permissions to empty array
          setRoles(prevRoles =>
            prevRoles.map(role =>
              role.id === roleId
                ? { ...role, permissions: [] }
                : role
            )
          );
        }
      } else {
        // If no permissions are assigned, set empty array
        setSelectedPermissions([]);
        // Update the role's permissions to empty array
        setRoles(prevRoles =>
          prevRoles.map(role =>
            role.id === roleId
              ? { ...role, permissions: [] }
              : role
          )
        );
      }
    } catch (err) {
      console.error("Error fetching role permissions:", err);
      setError("Failed to fetch role permissions. Please try again later.");
      setSelectedPermissions([]);
    } finally {
      setPermissionLoading(false);
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(search.toLowerCase())
  );

  // Initialize form when opening dialog
  useEffect(() => {
    if (open) {
      if (editMode && currentRole) {
        setFormData({
          name: currentRole.name,
        });
      } else {
        // Reset form for new role
        setFormData({
          name: "",
        });
      }
    }
  }, [open, editMode, currentRole]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAddRole = () => {
    setCurrentRole(null);
    setEditMode(false);
    setOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setCurrentRole(role);
    setEditMode(true);
    setOpen(true);
  };

  const handleDeleteRole = (role: Role) => {
    setCurrentRole(role);
    setDeleteDialogOpen(true);
  };

  const handleLinkRole = async (role: Role) => {
    setCurrentRole(role);
    setPermissionDialogOpen(true);

    // Fetch current permissions for this role
    await fetchRolePermissions(role.id);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteDialogOpen(false);
    setPermissionDialogOpen(false);
    setFormData({
      name: "",
    });
  };

  // Handle text input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle permission selection
  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Handle save role
  const handleSaveRole = async () => {
    if (formData.name.trim() === "") {
      // Validation could be improved
      console.error("Role name is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editMode && currentRole) {
        // Update existing role
        const payload = {
          id: currentRole.id,
          name: formData.name,
        };

        const response = await axiosInstance.put<ApiResponse>(
          `${ROLE_API}${currentRole.id}`,
          payload
        );

        if (response.data.status) {
          // After successful API call, fetch the updated roles
          await fetchRoles();
        } else {
          setError("Failed to update role: " + response.data.message);
        }
      } else {
        // Create new role
        const payload = {
          name: formData.name,
        };

        const response = await axiosInstance.post<ApiResponse>(
          ROLE_API,
          payload
        );

        if (response.data.status) {
          // After successful API call, fetch the updated roles
          await fetchRoles();
        } else {
          setError("Failed to create role: " + response.data.message);
        }
      }

      handleClose();
    } catch (err) {
      console.error("Error saving role:", err);
      setError("Failed to save role. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!currentRole) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.delete<ApiResponse>(
        `${ROLE_API}${currentRole.id}`
      );

      if (response.data.status) {
        // After successful API call, fetch the updated roles
        await fetchRoles();
      } else {
        setError("Failed to delete role: " + response.data.message);
      }

      handleClose();
    } catch (err) {
      console.error("Error deleting role:", err);
      setError("Failed to delete role. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle save role permissions
  const handleSaveRolePermissions = async () => {
    if (!currentRole) return;

    setLoading(true);
    setError(null);

    try {
      // Make API call to save role permissions
      const payload = {
        role_id: currentRole.id,
        permission_ids: selectedPermissions
      };

      const response = await axiosInstance.post<ApiResponse>(
        "/gpstracker/rolepermissionshandler/",
        payload
      );

      if (response.data.status) {
        // Update local state after successful API call
        setRoles(prevRoles =>
          prevRoles.map(role =>
            role.id === currentRole.id
              ? {
                ...role,
                permissions: selectedPermissions?.map(id => id.toString())
              }
              : role
          )
        );

        handleClose();
      } else {
        setError("Failed to save role permissions: " + response.data.message);
      }
    } catch (err) {
      console.error("Error saving role permissions:", err);
      setError("Failed to save role permissions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking admin status
  if (isAdmin === undefined) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.loadingSpinner}>
            <div className={styles.loadingText}>Checking permissions...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied for non-admin users
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerLeft}>
                <div>
                  <div className={styles.headerTitle}>
                    Access Denied
                  </div>
                  <div className={styles.headerSubtitle}>
                    You don't have permission to access Role Management. This feature is only available for administrators.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.alert}>
            <WarningIcon sx={{ fontSize: 18 }} />
            Role Management is restricted to administrators only. Please contact your system administrator if you need access to this feature.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              
              <div>
                <div className={styles.headerTitle}>
                  Role Management
                </div>
                <div className={styles.headerSubtitle}>
                  Configure organizational roles and access permissions
                </div>
              </div>
            </div>
            <button
              onClick={handleAddRole}
              className={styles.createButton}
            >
              <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Create Role
            </button>
          </div>
        </div>

        {/* Alert Section */}
        {error && (
          <div className={styles.alert}>
            <WarningIcon sx={{ fontSize: 18 }} />
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && !open && (
          <div className={styles.loadingSpinner}>
            <div className={styles.loadingText}>Loading roles...</div>
          </div>
        )}

      

        {/* Table Section */}
        {filteredRoles.length > 0 && (
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.tableTitle}>
                Role Overview Table
              </div>
                  <div className={styles.searchContainer}>
              <div className={styles.searchField}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search roles by name..."
                  value={search}
                  onChange={handleSearch}
                  className={styles.searchInput}
                />
              </div>
            </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th>Role Name</th>
                    <th>Role ID</th>
                    {/* <th>Permissions</th> */}
                 
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {filteredRoles.map((role) => (
                    <tr key={role.id}>
                      <td>
                        <div className={styles.roleNameCell}>
                          <div className={styles.tableRoleAvatar}>
                            <AdminIcon sx={{ fontSize: 18, color: 'white' }} />
                          </div>
                          <span className={styles.roleNameText}>{role.name}</span>
                        </div>
                      </td>
                      <td className={styles.roleIdCell}>
                        #{role.id}
                      </td>
                      {/* <td className={styles.permissionsCell}>
                        {role.permissions.length} permissions
                      </td> */}
                    
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            onClick={() => handleLinkRole(role)}
                            className={`${styles.tableActionButton} ${styles.tableLinkButton}`}
                            title="Link Permissions"
                          >
                            <LinkIcon sx={{ fontSize: 14 }} />
                          </button>
                          <button
                            onClick={() => handleEditRole(role)}
                            className={`${styles.tableActionButton} ${styles.tableEditButton}`}
                            title="Edit Role"
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </button>
                          <button
                            onClick={() => handleDeleteRole(role)}
                            className={`${styles.tableActionButton} ${styles.tableDeleteButton}`}
                            title="Delete Role"
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Role Modal */}
        {open && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2>{editMode ? "Edit Role" : "Add Role"}</h2>
                <button
                  onClick={handleClose}
                  className={styles.closeButton}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className={styles.modalContent}>
                {loading && (
                  <div className={styles.loadingSpinner}>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                )}

                {error && (
                  <div className={styles.alert}>
                    <WarningIcon sx={{ fontSize: 18 }} />
                    {error}
                  </div>
                )}

                <div className={styles.formField}>
                  <label>Role Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter role name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  onClick={handleClose}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRole}
                  disabled={loading || !formData.name.trim()}
                  className={styles.saveButton}
                >
                  {editMode ? "Update Role" : "Create Role"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteDialogOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2>Delete Role</h2>
                <button
                  onClick={handleClose}
                  className={styles.closeButton}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className={styles.modalContent}>
                {loading && (
                  <div className={styles.loadingSpinner}>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                )}

                <div className={styles.alert}>
                  <WarningIcon sx={{ fontSize: 18 }} />
                  Are you sure you want to delete the role <strong>"{currentRole?.name}"</strong>?
                </div>

                {currentRole && currentRole.users > 0 && (
                  <div className={styles.alert}>
                    <WarningIcon sx={{ fontSize: 18 }} />
                    This role is assigned to {currentRole.users} users. You must
                    reassign users before deleting this role.
                  </div>
                )}
              </div>
              <div className={styles.modalActions}>
                <button
                  onClick={handleClose}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={loading || !!(currentRole && currentRole.users > 0)}
                  className={styles.deleteButton}
                >
                  Delete Role
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Permission Management Modal */}
        {permissionDialogOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2>Manage Permissions</h2>
                <button
                  onClick={handleClose}
                  className={styles.closeButton}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className={styles.modalContent}>
                {permissionLoading && (
                  <div className={styles.loadingSpinner}>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                )}

                {error && (
                  <div className={styles.alert}>
                    <WarningIcon sx={{ fontSize: 18 }} />
                    {error}
                  </div>
                )}

                {!permissionLoading && permissions.length === 0 && (
                  <div className={styles.emptyState}>
                    <SecurityIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
                    <div className={styles.emptyTitle}>
                      No Permissions Available
                    </div>
                    <div className={styles.emptySubtitle}>
                      There are no permissions defined in the system yet.
                    </div>
                  </div>
                )}

                {!permissionLoading && permissions.length > 0 && (
                  <>
                    <div className={styles.alert}>
                      <SecurityIcon sx={{ fontSize: 18 }} />
                      Select the permissions you want to assign to this role. Users with this role will have access to the selected permissions.
                    </div>

                    <div className={styles.permissionsHeader}>
                      <h6>Available Permissions ({permissions.length})</h6>
                      <div className={styles.selectedChip}>
                        {selectedPermissions.length} selected
                      </div>
                    </div>

                    <div className={styles.permissionsList}>
                      {permissions.map((permission) => (
                        <div
                          key={permission.id}
                          className={`${styles.permissionItem} ${selectedPermissions.includes(permission.id) ? styles.permissionItemSelected : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                            className={styles.permissionCheckbox}
                          />
                          <div className={styles.permissionInfo}>
                            <div className={styles.permissionName}>
                              {permission.name}
                            </div>
                            <div className={styles.permissionId}>
                              Permission ID: {permission.id}
                            </div>
                          </div>
                          {selectedPermissions.includes(permission.id) && (
                            <CheckCircleIcon className={styles.selectedIcon} />
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.modalActions}>
                <button
                  onClick={handleClose}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRolePermissions}
                  disabled={loading || permissionLoading}
                  className={styles.saveButton}
                >
                  <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Save Permissions ({selectedPermissions.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;
